import { Box, Button, Typography, CircularProgress } from '@mui/material';
import React, { useEffect, useMemo, useState, useRef } from 'react';

import { WebSocketCallServiceConfig } from '@shared/services/websocketCallService';
import { ConversationListMessage } from '@widgets/Popup/MonitorCampaignV2/ConversationListMessage';

import { useWebSocketCallService } from '../../hooks/useWebSocketCallService';
import { ConversationLogDto } from '@shared/services/conversationApiService/apiService';
import { RootState, useAppSelector } from '@shared/store';
import { STOP_AUTOSCROLL_THRESHOLD_PX } from './constants';

interface WebSocketCallComponentProps {
  serviceUrl: string;
  campaignId: string;
  customerName?: string;
  onCallEnd?: () => void;
  setIsWSCallInProgress: (value: React.SetStateAction<boolean>) => void;
  setIsWSConnecting?: (value: React.SetStateAction<boolean>) => void;
}

export const WebSocketCallComponent: React.FC<WebSocketCallComponentProps> = ({
  serviceUrl,
  campaignId,
  customerName,
  onCallEnd,
  setIsWSCallInProgress,
  setIsWSConnecting,
}) => {
  const currentUser = useAppSelector((state: RootState) => state.auth.authenticatedUser);
  const [isConnecting, setIsConnecting] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesBoxRef = useRef<HTMLDivElement | null>(null);

  // Build service config once
  const config: WebSocketCallServiceConfig = useMemo(() => {
    const fullName = [currentUser?.firstName, currentUser?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    const displayName = customerName?.trim() || fullName || 'Anonymous';

    return {
      serviceUrl,
      campaignId,
      customerName: displayName,

      // Lifecycle callbacks as onConnectedToServer, onSessionReady, onTranscriptReceived could be placed here (service handles audio internally)
      onDisconnect: () => {
        onCallEnd?.();
      },
    };
  }, [serviceUrl, campaignId, customerName, currentUser, onCallEnd]);

  const {
    isConnected,
    isSessionReady,
    callId,
    error,
    transcripts,
    startCall,
    endCall,
    disconnect,
  } = useWebSocketCallService(config);

  // Mirror "in progress" flag to parent
  useEffect(() => {
    setIsWSCallInProgress(isConnected);
  }, [isConnected, setIsWSCallInProgress]);

  // Mirror "connecting" flag to parent
  useEffect(() => {
    setIsWSConnecting?.(isConnecting);

    // Cleanup: reset connecting state when component unmounts
    return () => {
      setIsWSConnecting?.(false);
    };
  }, [isConnecting, setIsWSConnecting]);

  // Start the call: connect WS + init audio + stream
  const handleStartCall = async () => {
    try {
      setIsConnecting(true);
      await startCall();
    } catch (e) {
      console.error('Failed to start call:', e);
    } finally {
      setIsConnecting(false);
    }
  };

  // End the call and cleanup
  const handleEndCall = async () => {
    try {
      await endCall();
    } finally {
      disconnect();
      onCallEnd?.();
    }
  };

  useEffect(() => {
    if (messagesBoxRef.current && messagesEndRef.current) {
      const scrollHeight = messagesBoxRef.current.scrollHeight;
      const clientHeight = messagesBoxRef.current.clientHeight;
      const scrolledFromTop = messagesBoxRef.current.scrollTop;

      if (scrollHeight - clientHeight - scrolledFromTop < STOP_AUTOSCROLL_THRESHOLD_PX) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [transcripts]);

  return (
    <Box
      sx={{
        mb: '14px',
        display: 'inline-flex',
        justifyContent: 'center',
        flexDirection: 'column',
      }}>
      {/* Status */}
      {callId && (
        <Box sx={{ mb: 2, alignSelf: 'center' }}>
          <Typography>Connection: {isConnected ? 'Connected' : 'Disconnected'}</Typography>
          <Typography>Session: {isSessionReady ? 'Ready' : 'Not Ready'}</Typography>
          <Typography>Call ID: {callId}</Typography>
          {error && <Typography color="red">Error: {error}</Typography>}
        </Box>
      )}

      {/* Controls */}
      <Box sx={{ mb: 2, alignSelf: 'center' }}>
        {!isConnected ? (
          <Button
            sx={{
              minWidth: 200,
              textTransform: 'none',
              fontWeight: 600,
            }}
            variant="contained"
            color="success"
            size="large"
            onClick={handleStartCall}
            disabled={isConnecting}>
            {isConnecting ? 'Connecting…' : 'Start Call'}
          </Button>
        ) : (
          <Button
            sx={{ mt: 2 }}
            onClick={handleEndCall}
            variant="contained"
            color="error"
            size="large">
            End Call
          </Button>
        )}
      </Box>

      {/* Loading State - Connected but waiting for session.ready */}
      {isConnected && !isSessionReady && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography>Joining session...</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please wait while we establish the call session
          </Typography>
        </Box>
      )}

      {/* Transcript */}
      {isSessionReady && (
        <Box sx={{ mt: 2, padding: '0 20px' }}>
          <Typography>Live Transcript</Typography>
          <Box
            sx={{
              border: '1px solid #ccc',
              p: 1.5,
              minHeight: 200,
              maxHeight: 400,
              overflowY: 'auto',
              whiteSpace: 'pre-wrap',
            }}
            ref={messagesBoxRef}>
            <Box sx={{ width: '100%', overflowX: 'hidden', overflowY: 'auto' }}>
              {transcripts && transcripts.length > 0
                ? transcripts.map((messageItem, idx) => (
                    <ConversationListMessage
                      key={idx}
                      actorName="Agent"
                      messageItem={messageItem as ConversationLogDto}
                      isStacked={false}
                    />
                  ))
                : 'Waiting for transcript…'}
              <div ref={messagesEndRef} />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
